// ═══════════════════════════════════════════════════════════════════════════════
// FILE: src/app/shared/dialogs/auth-error-dialog/auth-error-dialog.component.ts
// VERSION: v1.0.0 - Authentication Error Dialog
// DATE: 2025-01-09
//
// PURPOSE: Beautiful Material Dialog for session timeout / auth loss
// AUTO-REDIRECTS: After 5 seconds to login page
// ═══════════════════════════════════════════════════════════════════════════════

import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';

/**
 * Dialog Data Interface
 */
export interface AuthErrorDialogData {
  errorMessage?: string;        // Optional custom error message
  statusCode?: number;          // HTTP status code (401, 403)
  redirectDelay?: number;       // Delay in seconds (default: 5)
}

@Component({
  selector: 'app-auth-error-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule
  ],
  templateUrl: './auth-error-dialog.component.html',
  styleUrls: ['./auth-error-dialog.component.scss']
})
export class AuthErrorDialogComponent implements OnInit, OnDestroy {
  
  countdown: number;
  private countdownInterval: any;
  loginUrl: string;
  
  constructor(
    public dialogRef: MatDialogRef<AuthErrorDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AuthErrorDialogData,
    private router: Router
  ) {
    // Disable closing via backdrop or ESC key
    this.dialogRef.disableClose = true;
    
    // Set countdown (default: 5 seconds)
    this.countdown = data.redirectDelay || 5;
    
    // Construct login URL based on current hostname
    this.loginUrl = this.constructLoginUrl();
    
    console.log('[AuthErrorDialog] Opened with:', {
      statusCode: data.statusCode,
      countdown: this.countdown,
      loginUrl: this.loginUrl
    });
  }
  
  ngOnInit(): void {
    this.startCountdown();
  }
  
  ngOnDestroy(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }
  
  /**
   * Construct login URL based on current hostname
   * Examples:
   *   - localhost:4200 → http://localhost:4200/
   *   - tryout.ceeq.it → https://tryout.ceeq.it/
   *   - demo.ceeq.it → https://demo.ceeq.it/
   */
  private constructLoginUrl(): string {
    const hostname = window.location.hostname;
    
    if (hostname.includes('localhost')) {
      return 'http://localhost:4200/';
    }
    
    // For production: same subdomain, just root path
    return `https://${hostname}/`;
  }
  
  /**
   * Start countdown timer and redirect when done
   */
  private startCountdown(): void {
    this.countdownInterval = setInterval(() => {
      this.countdown--;
      
      if (this.countdown <= 0) {
        this.redirectToLogin();
      }
    }, 1000);
  }
  
  /**
   * Redirect to login page
   */
  private redirectToLogin(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    
    console.log('[AuthErrorDialog] Redirecting to login:', this.loginUrl);
    
    // Close dialog first
    this.dialogRef.close();
    
    // Use window.location for full page reload (clears all state)
    window.location.href = this.loginUrl;
  }
  
  /**
   * User clicked "Go to Login Now" button
   * Redirects immediately without waiting
   */
  onLoginNow(): void {
    console.log('[AuthErrorDialog] User clicked: GO TO LOGIN NOW');
    this.redirectToLogin();
  }
  
  /**
   * Get user-friendly error title based on status code
   */
  getErrorTitle(): string {
    if (!this.data.statusCode) {
      return 'Session Expired';
    }
    
    switch (this.data.statusCode) {
      case 401:
        return 'Session Expired';
      case 403:
        return 'Access Denied';
      default:
        return 'Authentication Required';
    }
  }
  
  /**
   * Get user-friendly error description
   */
  getErrorDescription(): string {
    if (this.data.errorMessage) {
      return this.data.errorMessage;
    }
    
    if (!this.data.statusCode) {
      return 'Your session has expired. Please log in again to continue.';
    }
    
    switch (this.data.statusCode) {
      case 401:
        return 'Your session has expired. Please log in again to continue.';
      case 403:
        return 'You don\'t have permission to access this resource. Please log in again.';
      default:
        return 'Authentication is required. Please log in to continue.';
    }
  }
  
  /**
   * Get icon name based on status code
   */
  getIconName(): string {
    if (!this.data.statusCode) {
      return 'schedule';
    }
    
    switch (this.data.statusCode) {
      case 401:
        return 'schedule';
      case 403:
        return 'lock';
      default:
        return 'warning';
    }
  }
}

/* END OF FILE */
